import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { FieldLabel } from "../shared/FieldHint";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const TAX_CLASSIFICATIONS = ["Individual/Sole Proprietor", "Single-Member LLC", "C-Corp", "S-Corp", "Partnership", "Trust/Estate"];
const ACCOUNT_TYPES = ["Checking", "Savings"];
const tinTypeFor = (taxClassification) => (taxClassification === "Individual/Sole Proprietor" ? "SSN" : "EIN");

const BLANK = { legal_name: "", address: "", bank_name: "", ach_routing_number: "", ach_account_number: "", account_type: "Checking", tax_classification: "Individual/Sole Proprietor", tin_number: "" };

export function ManagerPaymentTaxTab({ onSubmitted, heading, subheading } = {}) {
  const { user } = useAuth();
  const readOnly = !!user?._impersonating;
  const [existing, setExisting] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [justSubmitted, setJustSubmitted] = useState(false);

  useEffect(() => {
    api.get("/manager/payment-info")
      .then(({ data }) => setExisting(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    if (!form.legal_name.trim() || !form.address.trim() || !form.bank_name.trim() || !form.ach_routing_number.trim() || !form.ach_account_number.trim() || !form.tin_number.trim()) {
      toast.error("Legal name, mailing address, bank name, ACH routing/account, and tax ID are required");
      return;
    }
    if (!confirmed) {
      toast.error("Check the box confirming this information is accurate before submitting");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/manager/payment-info", { ...form, tin_type: tinTypeFor(form.tax_classification), confirmed });
      toast.success("Payment & tax info saved");
      setJustSubmitted(true);
      const { data } = await api.get("/manager/payment-info");
      setExisting(data);
      setForm(BLANK);
      setConfirmed(false);
      onSubmitted?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <div data-testid="manager-payment-tax-tab" className="max-w-2xl">
      {justSubmitted && existing && (
        <div className="bg-igc-success/10 border border-igc-success/30 p-4 mb-6 text-igc-success text-sm" data-testid="manager-payment-confirmation-banner">
          Your payment & tax info was submitted successfully and is now on file — no further action needed.
        </div>
      )}
      {existing && (
        <div className="bg-igc-surface border border-igc-gold/15 p-5 mb-8" data-testid="manager-payment-info-on-file">
          <p className="text-igc-goldText text-xs uppercase tracking-widest mb-3">On File</p>
          <p className="text-igc-cream text-sm">Legal name: {existing.legal_name}</p>
          <p className="text-igc-cream text-sm">Bank: {existing.bank_name}</p>
          <p className="text-igc-cream text-sm">ACH routing: {existing.ach_routing_masked}</p>
          <p className="text-igc-cream text-sm">ACH account: {existing.ach_account_masked} ({existing.account_type})</p>
          <p className="text-igc-cream text-sm">Tax ID ({existing.tin_type}): {existing.tin_masked}</p>
          <p className="text-igc-muted text-xs mt-2">Resubmit below any time to update your info on file.</p>
        </div>
      )}

      <h2 className="font-serif text-xl text-igc-cream tracking-tight mb-4">{heading || (existing ? "Update Payment & Tax Info" : "Submit Payment & Tax Info")}</h2>
      {subheading && <p className="text-igc-muted text-sm mb-4">{subheading}</p>}
      {readOnly && <p className="text-igc-muted text-xs mb-3" data-testid="manager-payment-readonly-notice">Read-only in View As mode — this form can't be submitted while impersonating.</p>}
      <form onSubmit={submit} className="space-y-3 bg-igc-surface border border-igc-gold/15 p-5">
        <div>
          <FieldLabel testId="manager-payment-legal-name-label" className="mb-1 block">Legal Name</FieldLabel>
          <Input data-testid="manager-payment-legal-name" aria-label="Legal full name" placeholder="Legal full name" disabled={readOnly} value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
        </div>
        <div>
          <FieldLabel testId="manager-payment-address-label" className="mb-1 block">Mailing Address</FieldLabel>
          <Input data-testid="manager-payment-address" aria-label="Mailing address" placeholder="Mailing address" disabled={readOnly} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
        </div>
        <div>
          <FieldLabel testId="manager-payment-bank-name-label" className="mb-1 block">Bank Name</FieldLabel>
          <Input data-testid="manager-payment-bank-name" aria-label="Bank name" placeholder="Bank name" disabled={readOnly} value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
        </div>
        <div>
          <FieldLabel hint="Where your commission payments are sent by direct deposit. Both numbers are encrypted and never shown in full again." testId="manager-payment-ach-hint" className="mb-1 block">ACH Routing & Account</FieldLabel>
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="manager-payment-ach-routing" aria-label="ACH routing number" placeholder="ACH routing number" disabled={readOnly} value={form.ach_routing_number} onChange={(e) => setForm({ ...form, ach_routing_number: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
            <Input data-testid="manager-payment-ach-account" aria-label="ACH account number" placeholder="ACH account number" disabled={readOnly} value={form.ach_account_number} onChange={(e) => setForm({ ...form, ach_account_number: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
          </div>
        </div>
        <div>
          <FieldLabel hint="Is the account above a checking or savings account?" testId="manager-payment-account-type-hint" className="mb-1 block">Account Type</FieldLabel>
          <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })} disabled={readOnly}>
            <SelectTrigger data-testid="manager-payment-account-type" className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
              {ACCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel hint="How the IRS classifies you for tax purposes — pick the one that matches how you filed your business." testId="manager-payment-tax-classification-hint" className="mb-1 block">Tax Classification</FieldLabel>
          <Select value={form.tax_classification} onValueChange={(v) => setForm({ ...form, tax_classification: v })} disabled={readOnly}>
            <SelectTrigger data-testid="manager-payment-tax-classification" className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
              {TAX_CLASSIFICATIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel hint="SSN if you're an individual/sole proprietor, EIN if you have a registered business entity. Determined automatically by your tax classification above." testId="manager-payment-tin-type-hint" className="mb-1 block">{tinTypeFor(form.tax_classification)}</FieldLabel>
          <Input data-testid="manager-payment-tin-number" aria-label={tinTypeFor(form.tax_classification)} placeholder={tinTypeFor(form.tax_classification) === "EIN" ? "EIN" : "SSN"} disabled={readOnly} value={form.tin_number} onChange={(e) => setForm({ ...form, tin_number: e.target.value })} className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream" />
        </div>
        <label className="flex items-start gap-2 text-igc-cream text-sm pt-1" data-testid="manager-payment-confirm-label">
          <Checkbox data-testid="manager-payment-confirm-checkbox" checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} disabled={readOnly} className="mt-0.5" />
          <span>I confirm the information above is accurate and belongs to me, and I authorize IGC to use it for commission payment processing.</span>
        </label>
        <Button data-testid="manager-payment-submit-button" type="submit" disabled={submitting || readOnly || !confirmed} title="Saves your payment and tax info securely — used only to process your commission payouts." className="bg-igc-gold hover:bg-igc-goldHover text-black disabled:opacity-40 disabled:cursor-not-allowed">Submit</Button>
      </form>
    </div>
  );
}
